import type { Lane, NurseTask } from './api';
import { clock, drName, mmss, secondsLeft, shortName, taskLabel } from './format';

export type DayFilter = 'open' | 'handed' | 'all';

/**
 * One row of the day. Usually one task; a BATCH is the tasks one room sent out
 * together ("Karim F. — 2 now-tasks"), which the patient waits on as one thing
 * and which returns them to the doctor only when all of it is finished.
 */
export interface DayItem {
  id: string;
  lane: Lane;
  primary: NurseTask;
  tasks: NurseTask[];
  batch: boolean;
}

const LANE_ORDER: Lane[] = ['rooms', 'prep', 'after'];
const STATE_ORDER: Record<NurseTask['state'], number> = { done: 0, cant: 1, in_progress: 2, open: 3 };
const isOpen = (t: NurseTask) => t.state === 'open' || t.state === 'in_progress';

export function inFilter(t: NurseTask, f: DayFilter): boolean {
  if (f === 'handed') return t.handedByMe;
  if (f === 'open') return t.mine && isOpen(t);
  return true;
}

export function buildItems(tasks: NurseTask[], f: DayFilter): DayItem[] {
  const items: DayItem[] = [];
  const batches = new Map<string, NurseTask[]>();
  for (const t of tasks) {
    if (t.batchId && t.mine) {
      const list = batches.get(t.batchId) ?? [];
      list.push(t);
      batches.set(t.batchId, list);
    }
  }
  const seen = new Set<string>();
  for (const t of tasks) {
    if (t.batchId && t.mine && (batches.get(t.batchId)?.length ?? 0) > 1) {
      if (seen.has(t.batchId)) continue;
      const group = batches.get(t.batchId) ?? [];
      const live = group.some(isOpen);
      if (f === 'handed' || (f === 'open' && !live)) continue;
      seen.add(t.batchId);
      /* Done first, then running, then waiting — "injection ✓ · dressing in
         progress". A room sends its now-tasks out together, so they share a
         createdAt and the API's order would decide it at random. */
      group.sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state]
        || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        || a.id.localeCompare(b.id));
      const primary = group.find(isOpen) ?? group[0];
      items.push({ id: `batch:${t.batchId}`, lane: primary.lane, primary, tasks: group, batch: true });
      continue;
    }
    if (!inFilter(t, f)) continue;
    items.push({ id: t.id, lane: t.lane, primary: t, tasks: [t], batch: false });
  }
  const first = (i: DayItem) => Math.min(...i.tasks.map((t) => new Date(t.createdAt).getTime()));
  return items.sort(
    (a, b) => LANE_ORDER.indexOf(a.lane) - LANE_ORDER.indexOf(b.lane) || first(a) - first(b),
  );
}

/**
 * The search on My day. ONE matcher for the table and the docked pane, so the
 * pane can never dock a task the table has filtered away.
 */
export function matchesQuery(item: DayItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return item.tasks.some((t) =>
    [taskLabel(t), t.orderText, t.detail, t.room, drName(t.requestedBy),
      t.patient ? `${t.patient.firstName} ${t.patient.lastName}` : null]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q)));
}

/** The rows My day shows for a filter and a search. */
export function dayRows(tasks: NurseTask[], f: DayFilter, query: string): DayItem[] {
  return buildItems(tasks, f).filter((i) => matchesQuery(i, query));
}

/** Lane order, then oldest first — the order My day reads in. */
export function byLane(a: NurseTask, b: NurseTask): number {
  return LANE_ORDER.indexOf(a.lane) - LANE_ORDER.indexOf(b.lane)
    || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
}

/** A primitive that changes whenever any row's state does — see useShellChrome. */
export function signature(items: DayItem[]): string {
  return items
    .map((i) => `${i.id}:${i.tasks.map((t) => `${t.state}${t.dueAt ?? ''}${t.timerSeconds ?? ''}`).join(',')}`)
    .join('|');
}

export const LANE_CHIP: Record<Lane, { label: string; kind: 'warn' | 'soft' | 'outline' }> = {
  rooms: { label: 'From the rooms', kind: 'warn' },
  prep: { label: 'Prep', kind: 'soft' },
  after: { label: 'After the visit', kind: 'outline' },
};

const isLive = (t: NurseTask) => (t.state === 'open' || t.state === 'in_progress');

/** The row's title, as the table draws it — "Sara M. — dilating". */
export function rowTitle(i: DayItem): string {
  const t = i.primary;
  if (i.batch) return `${shortName(t.patient)} — ${i.tasks.length} now-tasks`;
  if (t.kind === 'dilate' && t.state === 'in_progress') return `${shortName(t.patient)} — dilating`;
  return taskLabel(t);
}

/**
 * The pane's overline — the lane, then what KIND of work this row is, as the
 * canvas writes it per row: "From the rooms · running", "Prep · before the
 * doctor", "After the visit · signed plan", "After the visit · closed"…
 */
export function paneOverline(i: DayItem): string {
  const t = i.primary;
  const lane = LANE_CHIP[i.lane].label;
  if (t.handedByMe) return `${lane} · handed over`;
  if (!i.tasks.some(isLive)) return `${lane} · closed`;
  if (i.batch) return `${lane} · parallel`;
  if (i.lane === 'rooms') return t.state === 'in_progress' && t.dueAt ? `${lane} · running` : `${lane} · live`;
  if (i.lane === 'prep') return `${lane} · before the doctor`;
  if (t.nurseOnly) return `${lane} · nurse-only`;
  const word: Partial<Record<NurseTask['kind'], string>> = {
    injection: 'signed plan', observe: 'observation', consent: 'blocking', chaperone: 'presence',
  };
  return word[t.kind] ? `${lane} · ${word[t.kind]}` : lane;
}

/** The search overlay's quiet line for a task — "From the rooms · ⏱ 12:00 running". */
export function searchCap(i: DayItem, now: number): string {
  const t = i.primary;
  const lane = LANE_CHIP[i.lane].label;
  if (t.handedByMe) return `${lane} · handed to ${shortName(t.assignee)}`;
  if (!i.tasks.some(isLive)) {
    return t.state === 'cant' ? `${lane} · came back · ${clock(t.completedAt)}` : `${lane} · done ✓ ${clock(t.completedAt)}`;
  }
  if (i.batch) return `${lane} · parallel · ${i.tasks.filter((x) => !isLive(x)).length} of ${i.tasks.length}`;
  const left = secondsLeft(t, now);
  if (t.state === 'in_progress' && left !== null) return `${lane} · ⏱ ${mmss(left)} running`;
  return [paneOverline(i), drName(t.requestedBy)].filter(Boolean).join(' · ');
}
